import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { isAndroidApp } from '../platform'

/**
 * The router path an App Links intent should land on. The filter only matches
 * https://provjericijene.hr/results/…, and the site and the app share one route
 * table, so the URL's own path is the answer.
 */
export function pathFromDeepLink(url) {
  try {
    const { pathname, search } = new URL(url)
    return `${pathname}${search}`
  } catch {
    return null
  }
}

/**
 * Deep links and the hardware back button, both no-ops outside the shell.
 *
 * Without a backButton listener Android exits the app from every screen. With
 * one and no fallback it is worse: ScannerPage navigates to the results with
 * `replace: true`, so that screen has no history entry and back would do
 * nothing at all. So: anywhere but the scanner goes to the scanner, and the
 * scanner exits.
 */
export function useAndroidShell() {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  // The listener is registered once, so it cannot close over the pathname.
  const pathRef = useRef(pathname)
  pathRef.current = pathname

  useEffect(() => {
    if (!isAndroidApp) return undefined

    let cancelled = false
    const handles = []

    import('@capacitor/app').then(({ App }) => {
      if (cancelled) return

      const register = (event, handler) =>
        App.addListener(event, handler).then((handle) => {
          if (cancelled) handle.remove()
          else handles.push(handle)
        })

      // Retained by the plugin, so a cold start from a link works too.
      register('appUrlOpen', ({ url }) => {
        const path = pathFromDeepLink(url)
        if (path) navigate(path)
      })

      register('backButton', () => {
        if (pathRef.current !== '/') navigate('/')
        else App.exitApp()
      })
    })

    return () => {
      cancelled = true
      handles.forEach((handle) => handle.remove())
    }
  }, [navigate])
}
